import { useCallback, useEffect, useRef, useState } from 'react';
import {
    fetchExerciseAvailability,
    fetchBpPermissionStatus,
    fetchBpReadings,
    requestBpPermission,
} from '../bridge/healthBridge';
import axiosApi from '../api/daonAxios';
import { getBloodPressureResultCode, splitLocalDateTime } from '../utils/bp';
import { getUsrInfoId } from '../utils/user';

/** 워치(삼성 헬스 모니터) 혈압 기록 동기화 */

const BACKFILL_DAYS = 30;
const REFRESH_DAYS = 7;

/** 삼성 헬스 모니터가 저장한 기록 = 워치로 잰 혈압 */
const APP_ID_HEALTH_MONITOR = 'com.samsung.android.shealthmonitor';

/** 연동 가능 상태 */
export const BP_ACCESS = Object.freeze({
    CHECKING: 'CHECKING',
    NOT_NATIVE: 'NOT_NATIVE',
    UNSUPPORTED: 'UNSUPPORTED',
    NOT_INSTALLED: 'NOT_INSTALLED',
    NEED_PERMISSION: 'NEED_PERMISSION',
    READY: 'READY',
});

const backfillKey = () => `iitp.bp.backfilled.${getUsrInfoId()}`;

let syncInFlight = null;

const pad = (n) => String(n).padStart(2, '0');
const toLocalDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d;
};

/** 삼성 헬스 기록 1건 → 서버 저장 형식. 못 쓰는 기록이면 null */
const toSaveItem = (r) => {
    const at = splitLocalDateTime(r?.time);
    if (!at) return null;

    const brssrTop = r.systolic;
    const brssrLwet = r.diastolic;
    if (!brssrTop || !brssrLwet) return null;

    return {
        ...at,
        brssrTop,
        brssrLwet,
        rsltCd: getBloodPressureResultCode(brssrTop, brssrLwet),
        cntcRecAt: '1',
    };
};

const saveBp = (saveObj) =>
    axiosApi.post('/bp', saveObj, {
        headers: { Authorization: `Bearer ${localStorage.getItem('jToken')}` },
    });

export function useWatchBpSync(onSynced) {
    const aliveRef = useRef(true);
    const onSyncedRef = useRef(onSynced);
    onSyncedRef.current = onSynced;

    const [access, setAccess] = useState(BP_ACCESS.CHECKING);

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    /** 동의 화면 없이 현재 상태만 확인 */
    const checkAccess = useCallback(async () => {
        let availability;
        try {
            availability = await fetchExerciseAvailability();
        } catch (e) {
            return e?.code === 'NOT_NATIVE' ? BP_ACCESS.NOT_NATIVE : BP_ACCESS.UNSUPPORTED;
        }

        if (!availability?.available) {
            return availability.reason === 'APP_NOT_INSTALLED'
                ? BP_ACCESS.NOT_INSTALLED
                : BP_ACCESS.UNSUPPORTED;
        }

        try {
            const { granted } = await fetchBpPermissionStatus();
            return granted ? BP_ACCESS.READY : BP_ACCESS.NEED_PERMISSION;
        } catch (e) {
            return BP_ACCESS.UNSUPPORTED;
        }
    }, []);

    /** 삼성 헬스에서 읽어 서버 저장. 새로 저장한 건수 반환 */
    const pull = useCallback(async () => {
        const backfilled = localStorage.getItem(backfillKey()) === '1';
        const spanDays = backfilled ? REFRESH_DAYS : BACKFILL_DAYS;

        const readings = await fetchBpReadings(
            toLocalDate(daysAgo(spanDays - 1)),
            toLocalDate(daysAgo(0)),
        );

        const list = (readings || [])
            .filter((r) => r?.appId === APP_ID_HEALTH_MONITOR)
            .map(toSaveItem)
            .filter(Boolean);

        // 0건이면 소급 완료로 치지 않는다. 워치 동기화가 아직 안 끝났을 수 있다
        if (list.length === 0) return 0;

        let savedCnt = 0;
        for (const item of list) {
            try {
                const res = await saveBp(item);
                if (res?.data?.result) savedCnt += 1;
            } catch (e) {
                console.warn('[bp] 워치 기록 저장 실패 —', item.recDe, item.recTm, e?.message);
            }
        }

        if (!backfilled) localStorage.setItem(backfillKey(), '1');

        return savedCnt;
    }, []);

    const sync = useCallback(async () => {
        if (!syncInFlight) {
            syncInFlight = pull().finally(() => {
                syncInFlight = null;
            });
        }

        try {
            const savedCnt = await syncInFlight;
            if (aliveRef.current && savedCnt > 0) onSyncedRef.current?.(savedCnt);
        } catch (e) {
            console.warn('[bp] 워치 혈압 동기화 실패 —', e?.message);
        }
    }, [pull]);

    /** 상태 재확인 + 가능하면 동기화 */
    const refresh = useCallback(async () => {
        const next = await checkAccess();
        if (!aliveRef.current) return next;
        setAccess(next);
        if (next === BP_ACCESS.READY) sync();
        return next;
    }, [checkAccess, sync]);

    /** 삼성 헬스 동의 화면 표시. 화면의 안내 모달에서만 호출 */
    const askPermission = useCallback(async () => {
        let granted = false;
        try {
            granted = !!(await requestBpPermission())?.granted;
        } catch (e) {
            console.warn('[bp] 혈압 권한 요청 실패 —', e?.message);
        }
        if (!aliveRef.current) return granted;
        setAccess(granted ? BP_ACCESS.READY : BP_ACCESS.NEED_PERMISSION);
        if (granted) sync();
        return granted;
    }, [sync]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // 앱 복귀 시 재조회
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [refresh]);

    return { access, askPermission, sync, refresh };
}
